import TextInput from '../../lib/TextField';
import en from '../../lang/en-us';
import { connect, useDispatch } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { onLogout, updateFirstTimeSettings } from '../../redux-store/actions/login';
import { AuthState, LOGOUT_SUCCESS } from '../../redux-store/types/login';
import { useDeviceAuthentication } from '../../lib/Authentication';
import { useState, useEffect, useCallback } from 'react';
import { mobileLogin } from '../../redux-store/actions/mobile';
import { MobileLoginBody } from '../../redux-store/types/mobile';
import { EncodeBase64 } from '../../utils/utilityFunctions';
import { CircularProgress, Dialog } from '@material-ui/core';
import SecureInput from '../../lib/TextField/SecureInput';
import { isMobileDevice } from '../../utils/constants';

const { loginAuth } = en;

interface PinProps extends AuthState {
    updateFirstTimeSettings: (body: Partial<AuthState>) => void;
    mobileLogin: (body: MobileLoginBody) => Promise<any>;
    onLogout: (body: any, ignore?: boolean) => Promise<any>;
    manualLogout: boolean;
}

function LoginPin(props: PinProps) {
    const { askAuthentication, authenticated, failedAttempts } = useDeviceAuthentication({
        enableAuthentication: !!props.enableTwoFactorAuth,
        initAuthentication: true,
    });
    const [pin, setPin] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [forgot, setForgot] = useState<boolean>(false);
    const dispatch = useDispatch();
    const authenticate = () => {
        if (!props.enableTwoFactorAuth) {
            alert('Please Enable Fingerprint in myprofile page.');
            return;
        }
        askAuthentication();
    };

    const _isMobile = isMobileDevice();

    //logging out previous session if exists
    useEffect(() => {
        async function logout() {
            if (_isMobile) {
                const payload = {
                    pan: props.pan,
                    pekrn: props.pekrn,
                    mobile: props.mobile,
                };
                await props.onLogout(payload, true);
            }
        }
        if (props.token) {
            logout();
        }
    }, []);

    // waiting for logout call to enable authentication
    useEffect(() => {
        if (!props.token && !props.manualLogout) {
            askAuthentication();
        }
        setLoading(!!props.token);
    }, [props.token, props.manualLogout]);

    const validateUser = useCallback(
        async (pin: string = '') => {
            setLoading(true);
            const _window: any = window;
            const { platform, version, uuid, model } = _window.device;
            try {
                let res = await props.mobileLogin({
                    userId: props.pan,
                    deviceId: EncodeBase64(uuid),
                    pin: EncodeBase64(pin),
                    metaData: {
                        appType: 'MOBILE',
                        OSVersion: version,
                        deviceName: model,
                        deviceOS: platform,
                    },
                });
                if (res) {
                    isVerified(true);
                }
            } catch (error: any) {
                alert(error.message);
            } finally {
                setLoading(false);
            }
        },
        [props],
    );

    useEffect(() => {
        if (authenticated && !props.token) {
            validateUser();
        }
    }, [authenticated]);

    const isVerified = (isAuthenticated: boolean) =>
        props.updateFirstTimeSettings({ isAutheticatedByDeviceOrPin: isAuthenticated });

    const validatePin = async (pin: string) => {
        if (pin.length === 4) {
            await validateUser(pin);
            return;
        }
        setPin(pin);
    };

    const onForgotPin = () => {
        setForgot(true);
        setTimeout(() => {
            setForgot(false);
            dispatch({ type: LOGOUT_SUCCESS });
        }, 2000);
    };

    return (
        <div className="mobile_mode login-auth">
            <div className="logo">
                <img src="/images/logo.svg" alt="logo" />
            </div>
            <div className="login-auth-content">
                <h3>
                    {loginAuth.Hi} {props.displayName}
                </h3>
                <SecureInput
                    value={pin}
                    onChange={({ target: { value } }) => validatePin(value)}
                    label={loginAuth.enterPin}
                    inputProps={{ inputMode: 'numeric' }}
                />
                <div className="forgot-pin">
                    <a onClick={onForgotPin}>{loginAuth.forgotpin}</a>
                </div>
                <div className="login-with-fingerprint">
                    <div className="fingerprint-or">
                        <span>{loginAuth.or}</span>
                    </div>
                    <a onClick={authenticate}>
                        <img src="/images/fingerprint-small.svg" alt="icon" />
                        {loginAuth.loginWithFingerprint}
                    </a>
                </div>
            </div>
            <Dialog maxWidth={'sm'} open={loading} aria-labelledby="enable-fingerprint-modal">
                <div className="enable-fingerprint-modal">
                    <CircularProgress />
                    <p>{'Please wait...'}</p>
                </div>
            </Dialog>
            <Dialog maxWidth={'sm'} open={forgot} aria-labelledby="enable-fingerprint-modal">
                <div className="enable-fingerprint-modal">
                    <CircularProgress />
                    <p>{'Please login again to set the new PIN'}</p>
                </div>
            </Dialog>
        </div>
    );
}

export default connect(
    ({ authReducer, authentication }: RootStateType) => ({ ...authReducer, ...authentication }),
    {
        updateFirstTimeSettings,
        mobileLogin,
        onLogout,
    },
)(LoginPin);
